import { captureException } from '@sentry/nuxt';
import type { ChargebeePaymentIntent as ChargebeePaymentIntentMsg } from 'gportal-grpc/gportal/api/payment/v1/responses_pb';
import { paymentFormTheme as palette } from '#variant/helpers/paymentForm';
import { mergeCssRules } from '~/helpers/paymentFormTheme';

const CHARGEBEE_LOAD_TIMEOUT_MS = 15_000;
const CHARGEBEE_POLL_INTERVAL_MS = 100;
const CHARGEBEE_SCRIPT_SRC = 'https://js.chargebee.com/v2/chargebee.js';

/**
 * Rejection reason an onButtonClick gate throws to block submission for a
 * client-side validation reason (e.g. the checkout consent checkbox isn't
 * ticked) rather than a payment failure. chargebee routes an onButtonClick
 * rejection into the component onError; these are recognised there and skip the
 * generic payment-error toast + Sentry capture, so a user who simply hasn't met
 * a pre-submit requirement (incl. via keyboard activation, which the overlay
 * can't intercept) sees only the host's own inline message.
 */
export class ChargebeeSubmitBlocked extends Error {
  constructor(message = 'submit blocked') {
    super(message);
    this.name = 'ChargebeeSubmitBlocked';
  }
}

/**
 * Recognises a ChargebeeSubmitBlocked even when chargebee has re-thrown /
 * wrapped the onButtonClick rejection reason (which breaks `instanceof`), so
 * the "submission blocked" signal never surfaces as a payment-error toast.
 */
function isSubmitBlocked(error: unknown): boolean {
  return (
    error instanceof ChargebeeSubmitBlocked ||
    (typeof error === 'object' &&
      error !== null &&
      (error as { name?: unknown }).name === 'ChargebeeSubmitBlocked')
  );
}

/**
 * Reports a chargebee failure to BOTH the browser console (plainly, with the
 * raw error object) and Sentry. The user-facing toast and chargebee's own
 * inline message (e.g. the generic "AN_ERROR_OCCURRED" it renders for a failed
 * authorization) are deliberately vague, so this console.error is the only
 * place the real cause — a declined card, a gateway rejection, a misconfigured
 * method — is legible while debugging. Signature mirrors Sentry's
 * captureException(error, hint) so every call site routes through here; the
 * `context` in the hint doubles as the console label. Note: errors swallowed by
 * the isSubmitBlocked() guard (the consent gate) return BEFORE reaching here, so
 * they stay quiet by design — only genuine failures are logged.
 */
function reportChargebeeError(
  error: unknown,
  hint: { extra: { context: string; [key: string]: unknown } },
) {
  console.error(`[useChargebeePayment] ${hint.extra.context}`, error);
  captureException(error, hint);
}

/**
 * The methods the Chargebee `payment` component may offer, in display order —
 * passed as both `paymentMethods.allowed` and `sortOrder` — and the methods the
 * payment-button gets our label for. A method enabled in the Chargebee site but
 * missing here is never shown, so a method switched on in Chargebee also needs
 * adding here. Of the ones listed, Chargebee still renders only those enabled
 * for the intent's business entity, gateway and currency (Smart Routing).
 * Values are chargebee's wire enum (snake_case).
 *
 * `klarna` is Klarna via Stripe. Adyen's `klarna_pay_now` is deliberately not
 * listed: its row reads `context.cart.lineItems`, whose documented schema has
 * no amounts and which chargebee.js strips to that schema, so Adyen always
 * answers "No InvoiceLines provided".
 */
const PAYMENT_METHODS = ['card', 'paypal_express_checkout', 'klarna'];
// WeChat Pay and Alipay are deliberately not listed: neither is saved as a
// payment source, and every checkout here starts a subscription, so they would
// pay the first invoice and leave the renewals with nothing to collect from.
// Seen on gportal-test for both — WeChat Pay via Adyen (Chargebee marks it
// disable_payment_method_save) and Alipay via Stripe, whose recurring Alipay is
// an invite-only private preview this account is not in.

/**
 * The methods the support "add payment method" modal offers. Its setup intent
 * is for 0 € and carries no subscription, and Klarna via Stripe needs both an
 * amount and a subscription interval: on gportal-test its row opened and closed
 * at once with Chargebee's raw "AN_ERROR_OCCURED". WeChat Pay and Alipay cannot
 * be saved at all, so the modal stays card and PayPal.
 */
export const SETUP_PAYMENT_METHODS = ['card', 'paypal_express_checkout'];

/**
 * Best-effort read of the method type from chargebee's onPaymentMethodChange
 * payload — its shape isn't documented, so a plain string or an object
 * exposing `type` / `paymentMethodType` is accepted.
 */
function readPaymentMethodType(paymentMethod: unknown): string {
  if (typeof paymentMethod === 'string') return paymentMethod;
  if (paymentMethod && typeof paymentMethod === 'object') {
    const pm = paymentMethod as Record<string, unknown>;
    if (typeof pm.type === 'string') return pm.type;
    if (typeof pm.paymentMethodType === 'string') return pm.paymentMethodType;
  }
  return '';
}

// Lazy-load chargebee.js the first time any Chargebee surface mounts.
// chargebee.js is ~100KB and only used on /checkout/* + the support-page
// payment-method modal, so injecting it via nuxt.config app.head.script
// would tax every landing/marketing/game-detail page for no benefit.
// loadChargebeeScript is idempotent — repeat calls return the same
// promise so concurrent mountPaymentForm() callers don't double-inject.
//
// A load that fails or times out is forgotten rather than cached, and its
// <script> tag removed: a tag that has already fired `error` never fires
// again, so a retry that waited on it would hang to the timeout every time.
let chargebeeScriptPromise: Promise<void> | null = null;

function loadChargebeeScript(): Promise<void> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('chargebee.js requires a browser context'));
  }
  if (window.Chargebee) return Promise.resolve();
  if (chargebeeScriptPromise) return chargebeeScriptPromise;

  let el: HTMLScriptElement | null = null;
  const load = new Promise<void>((resolve, reject) => {
    document
      .querySelectorAll<HTMLScriptElement>(
        `script[src="${CHARGEBEE_SCRIPT_SRC}"]`,
      )
      .forEach((stale) => stale.remove());
    el = document.createElement('script');
    el.src = CHARGEBEE_SCRIPT_SRC;
    el.async = true;
    el.addEventListener('load', () => resolve(), { once: true });
    el.addEventListener(
      'error',
      () => reject(new Error('chargebee.js failed to load')),
      { once: true },
    );
    document.head.appendChild(el);
  });

  let timeoutHandle: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timeoutHandle = setTimeout(
      () => reject(new Error('chargebee.js did not load in time')),
      CHARGEBEE_LOAD_TIMEOUT_MS,
    );
  });

  chargebeeScriptPromise = Promise.race([load, timeout])
    .catch((error) => {
      chargebeeScriptPromise = null;
      el?.remove();
      throw error;
    })
    .finally(() => clearTimeout(timeoutHandle));
  return chargebeeScriptPromise;
}

/**
 * Removes every Chargebee component iframe a previous mount left in the page.
 *
 * tearDown() only removes chargebee.js's `#cb-container`. The payment and
 * payment-button components keep their iframes in "component hosts" appended
 * to document.body (`[data-cb-component-host]`), and a later mount() recovers
 * any it finds by name and re-attaches it, reloading its URL only when the
 * iframe has no window (chargebee.js recoverParkedHost / attachPreloadedToTarget).
 * That URL carries the business entity, so a checkout that went from one region
 * to the other on the same page kept the first one's iframe: an INT order showed
 * "I authorize Ociris GmbH …" while Chargebee.getInstance() said GPORTAL_US
 * (reproduced on dev-alex: INT checkout, switch to EUR, back to step 4).
 *
 * Called right after tearDown(), when this mount has just taken ownership, so
 * everything removed belongs to a form that no longer owns chargebee.js.
 */
function removeParkedComponentHosts(): void {
  document
    .querySelectorAll(
      '[data-cb-component-host], iframe[src^="https://js.chargebee.com/components/"]',
    )
    .forEach((node) => node.remove());
}

/**
 * Which mount owns chargebee.js right now. chargebee.js allows one live
 * instance per page, and every mountPaymentForm() takes the next number before
 * its tearDown + init. Only the owner may tear chargebee.js down again: a form
 * whose host unmounted late (the support modal closed and reopened, the
 * checkout re-minting its intent) must not destroy the one that replaced it.
 * Module-level because the instance is page-global, not per composable call.
 */
let currentMount = 0;

/**
 * Hex values of this build's `--gp-depth-*` CSS tokens. Chargebee
 * Payment Components consumes literal color strings — it doesn't
 * resolve CSS variables — so callers that need to match a specific
 * surface (e.g. the modal background) import these constants and pass
 * the right one into mountPaymentForm({ surfaceColor }).
 */
export const [GP_DEPTH_0, GP_DEPTH_1, GP_DEPTH_2, GP_DEPTH_3] = palette.depth;

/**
 * Rebuilds the snake_case payment_intent shape chargebee.js expects from
 * the proto/Connect-ES typed message. Generated TS exposes lowerCamelCase
 * accessors (id, currencyCode, ...), but
 * `chargebee.components.create('payment', { paymentIntent })` reads the
 * Chargebee REST schema (id, currency_code, ...).
 */
/* eslint-disable camelcase */
export function chargebeePaymentIntentFromProto(
  intent: ChargebeePaymentIntentMsg,
): Record<string, unknown> {
  return {
    id: intent.id,
    status: intent.status,
    currency_code: intent.currencyCode,
    amount: Number(intent.amount),
    gateway_account_id: intent.gatewayAccountId,
    expires_at: Number(intent.expiresAt),
    reference_id: intent.referenceId,
    payment_method_type: intent.paymentMethodType,
    success_url: intent.successUrl,
    failure_url: intent.failureUrl,
    created_at: Number(intent.createdAt),
    modified_at: Number(intent.modifiedAt),
    resource_version: Number(intent.resourceVersion),
    updated_at: Number(intent.updatedAt),
    customer_id: intent.customerId,
    gateway: intent.gateway,
    business_entity_id: intent.businessEntityId,
    object: 'payment_intent',
  };
}
/* eslint-enable camelcase */

export type MountPaymentFormArgs = {
  /**
   * payment_intent JSON returned by gRPC (CreateCheckoutPaymentIntent /
   * CreateSetupPaymentIntent). Passed verbatim to chargebee.components.
   */
  paymentIntent: Record<string, unknown>;
  /** Container the chargebee `payment` component (form + method picker) mounts into. */
  paymentMount: HTMLElement;
  /**
   * Container the chargebee `payment-button` component (the submit/pay
   * button that finalizes the payment_intent) mounts into. The two
   * chargebee components communicate internally — the button is what
   * triggers tokenization on the form.
   */
  buttonMount: HTMLElement;
  /**
   * Optional override for the submit-button text. chargebee.js defaults
   * to "Pay & Subscribe" (or amount-aware variants) which fits the
   * subscription-checkout flow. The support-page "Add payment method"
   * flow uses a setup intent (amount=0) and wants its own label —
   * passing a string here applies it across every payment-method option
   * in PAYMENT_METHODS.
   */
  submitButtonLabel?: string;
  /**
   * Optional override for the chargebee form's surface color. Defaults
   * to GP_DEPTH_1, which matches the checkout step's container. The
   * support modal sits on a depth-2 background, so it passes
   * GP_DEPTH_2 here to avoid the chargebee canvas looking like a
   * mismatched paste-in.
   */
  surfaceColor?: string;
  /**
   * Fires after chargebee.components onSuccess. Caller is responsible for
   * invoking the matching SubmitCheckout / SubmitPaymentMethodSetup RPC
   * with the payment_intent.id.
   */
  onSuccess: (paymentIntentId: string) => Promise<void> | void;
  /**
   * Fires the instant chargebee authorizes the intent — BEFORE the
   * caller's onSubmit gRPC round-trip begins. The form wrapper uses
   * this to hide the submit button immediately, closing the race where
   * a user could double-click during the ~100-2000ms gRPC window
   * between chargebee authorization and our backend's SubmitCheckout /
   * SubmitPaymentMethodSetup returning.
   */
  onSubmitted?: () => void;
  onError?: (error: unknown) => void;
  onClose?: () => void;
  /**
   * Pre-submit gate. chargebee.js calls this the moment the user clicks the
   * pay button, BEFORE it tokenizes/authorizes the payment_intent. Resolve to
   * let the submission proceed; reject to block it (nothing is charged/held).
   * The checkout step uses this to enforce the mandatory T&C/Privacy checkbox
   * so the card is never authorized without consent. Defaults to a resolved
   * promise (no gate) for callers like the support add-method flow.
   */
  onButtonClick?: () => Promise<void>;
  /**
   * Whether chargebee opens a method row by itself on mount (its default). The
   * checkout lists the customer's saved methods above the form and passes
   * false, so nothing new is selected until the customer picks a row.
   */
  selectDefaultPaymentMethod?: boolean;
  /**
   * Fires with chargebee's method type ("card", "klarna", ...) when the
   * customer opens a row, so the host can keep one selection across its own
   * rows and chargebee's.
   */
  onPaymentMethodChange?: (paymentMethodType: string) => void;
  /**
   * A recoverable in-form payment failure (declined card, a method that could
   * not authorize, ...). The caller shows our own translated message in place of
   * Chargebee's error banner, which the theme hides because it renders whatever
   * Chargebee's server sends — on gportal-test the raw key "AN_ERROR_OCCURED".
   * Without it, a toast says the same.
   */
  onPaymentError?: () => void;
  /**
   * The subscription the payment starts. Klarna via Stripe charges recurring
   * payments against a mandate, and Stripe refuses to create one without the
   * subscription's interval — see the `additionalData` comment in
   * mountPaymentForm. Other methods ignore it. Checkout flow only.
   */
  /**
   * The methods to offer, in display order. Defaults to PAYMENT_METHODS (the
   * checkout); the support modal passes SETUP_PAYMENT_METHODS.
   */
  paymentMethods?: string[];
  subscription?: {
    /** Chargebee billing period unit of the plan: day / week / month / year. */
    periodUnit: 'day' | 'week' | 'month' | 'year';
    /** Number of periodUnits between renewals. */
    period: number;
  };
  /**
   * The customer's billing details, prefilled into the chargebee `payment`
   * component's `context.billingAddress`. Alternative methods (Klarna, Alipay,
   * ...) render a billing-address form; prefilling it from data we already hold
   * spares the buyer re-entering it. Only non-empty fields are forwarded (an empty value
   * would blank a chargebee default / trip validation) — the buyer completes
   * anything missing. Keys map 1:1 to chargebee's billingAddress fields.
   * Sourced from CreateCheckoutPaymentIntentResponse.billing_address; checkout
   * flow only (the support add-method setup intent has no order/address).
   */
  billingAddress?: {
    firstName?: string;
    lastName?: string;
    email?: string;
    addressLine1?: string;
    city?: string;
    state?: string;
    zip?: string;
    /** ISO 3166-1 alpha-2 — method eligibility (Klarna) reads the country. */
    countryCode?: string;
  };
  /**
   * Asked after chargebee.js loads and after each component mounts. When it
   * returns true — the host unmounted meanwhile — the half-built form is closed
   * and mountPaymentForm resolves to null instead of a handle. Defaults to
   * never cancelled.
   */
  isCancelled?: () => boolean;
};

export type MountPaymentFormHandle = {
  /** Tears down the mounted components. Safe to call multiple times. */
  unmount: () => void;
};

/**
 * useChargebeePayment wraps chargebee.js Payment Components (the native-DOM
 * replacement for `cbInstance.openCheckout`). Both the support-page
 * "add payment method" form and the checkout page rely on this composable
 * so they share the same theme mapping and chargebee.js bootstrap logic.
 */
export default function useChargebeePayment() {
  const config = useRuntimeConfig();
  const site = (config.public.chargebee?.site as string | undefined) ?? '';
  const publishableKey =
    (config.public.chargebee?.publishableKey as string | undefined) ?? '';

  // Capture the i18n locale ref synchronously here — `useI18n()` must run
  // during setup, and mountPaymentForm awaits chargebee.js loading before
  // it would otherwise need the locale (after which the setup context is
  // gone and useI18n() would throw "Must be called at the top of a setup
  // function").
  const { locale, t } = useI18n();
  // Same setup-context requirement for notifications — we surface
  // chargebee payment errors (declined card, invalid CVC, ...) as a
  // toast from inside the chargebee callback, which fires long after
  // the surrounding component's setup has resolved.
  const { pushErrorNotification } = useNotifications();

  /**
   * Loads chargebee.js and inits a fresh instance for this mount, bound to the
   * intent's business entity. Returns null when the host was cancelled while
   * the script loaded — before anything was torn down, so a form that is
   * already live elsewhere stays untouched.
   */
  async function initChargebee(
    businessEntityId: string | undefined,
    isCancelled: () => boolean,
  ): Promise<{ instance: ChargebeeInstance; mountId: number } | null> {
    if (typeof window === 'undefined') {
      throw new Error('chargebee.js requires a browser context');
    }
    if (!site || !publishableKey) {
      throw new Error('Chargebee site or publishable key not configured');
    }

    await loadChargebeeScript();
    // The polling fallback covers the rare case where the script's load event
    // fires before the global is assigned (chargebee.js init is async after
    // script onload on some browsers).
    const start = Date.now();
    while (!window.Chargebee) {
      if (Date.now() - start >= CHARGEBEE_LOAD_TIMEOUT_MS) {
        throw new Error('chargebee.js failed to load');
      }
      await new Promise((resolve) =>
        setTimeout(resolve, CHARGEBEE_POLL_INTERVAL_MS),
      );
    }

    if (isCancelled()) return null;

    // Take ownership before tearing down whatever form held chargebee.js:
    // from here on that form's late unmount must leave this one alone.
    currentMount += 1;
    const mountId = currentMount;

    // tearDown clears any prior init on the page — chargebee.js throws "No
    // message target specified" on a second init. Always a fresh init, never a
    // cached instance: another mount may have torn the cached one down.
    window.Chargebee.tearDown?.();
    removeParkedComponentHosts();
    // Bind the JS instance to the payment_intent's business entity. Omitting
    // businessEntityId makes chargebee.js fall back to the site's DEFAULT
    // business entity (GPORTAL, Inc / US), so an EU customer would see the
    // wrong legal entity in the Payment Components form / 3DS notice even
    // though the intent is GPORTAL_EU. Passing it keeps the rendered entity
    // (and the selected gateway) in sync with the intent.
    const instance = window.Chargebee.init({
      site,
      publishableKey,
      ...(businessEntityId ? { businessEntityId } : {}),
    });
    return { instance, mountId };
  }

  /**
   * GPORTAL theme mapping for chargebee.components. The Chargebee Payment
   * Components style API only documents four variable keys
   * (`colorBackground`, `defaultFontFamily`, `spacing`, `accentIndicator`)
   * plus theme.{appearance, accentColor, radius, scaling}. Everything else
   * lives in `style.rules`, which lets us override the chargebee component
   * internals via their `.g-*` class selectors — that's how we paint the
   * payment-method cards, inputs, and submit button in the GPORTAL palette
   * (mirrors the tokens declared in app/assets/styles/main.css).
   *
   * Which methods render is decided by PAYMENT_METHODS and the Chargebee site
   * config; the theme just covers presentation.
   */
  function gportalThemeOptions(
    activeLocale?: string,
    surfaceColor: string = GP_DEPTH_1,
  ): ChargebeeComponentsOptions {
    const options: ChargebeeComponentsOptions = {
      ...(activeLocale ? { locale: activeLocale } : {}),
      style: {
        theme: {
          appearance: 'dark',
          accentColor: palette.accent,
          radius: 'large',
          scaling: '100%',
        },
        variables: {
          // The four canonical chargebee variables — anything else lands
          // in `rules` below.
          colorBackground: surfaceColor,
          defaultFontFamily: palette.fontFamily,
          spacing: 2,
          accentIndicator: palette.accent,
        },
        rules: {
          // Outer form card. Matches the surfaceColor so the chargebee
          // canvas blends with whatever container hosts it (checkout
          // step → depth-1, support modal → depth-2).
          '.g-Surface': {
            background: surfaceColor,
            color: palette.text,
            borderColor: GP_DEPTH_3,
          },
          // Payment method tabs (Card / PayPal / Klarna / ...).
          // depth-0 is darker than every supported surface so it always
          // reads as "recessed" against the form background.
          '.g-RadioCardsItem': {
            background: GP_DEPTH_0,
            borderColor: GP_DEPTH_3,
            color: palette.text,
          },
          '.g-RadioCardsItem:hover': {
            borderColor: palette.accent,
          },
          '.g-RadioCardsItem[data-state="checked"]': {
            borderColor: palette.accent,
            background: surfaceColor,
          },
          // The accordion's method rows, as the website's selection cards
          // (frontend-conventions §7/§8) so they read like the saved-method
          // rows the checkout renders above this form: a 2px depth-3 border on
          // depth-2, the brand border with a 5% brand wash on hover, a 10% wash
          // once the row is open, and separate cards with a gap rather than one
          // list with dividers.
          //
          // Written against the accordion markup in chargebee's component
          // bundle (js.chargebee.com/components/v1/payment):
          //
          //   .payment-method-wrapper           accordion root
          //     div.<type>                      one per method (.card, .klarna…)
          //       [data-state=open|closed]      accordion item
          //         header > .g-Section > button     trigger; the Section pads
          //           .payment-method-wrapper-icon   it ~64px top and bottom
          //         [role=region]               the opened method's form
          //       .g-Separator                  divider, on all but the last
          //
          // Only the class hooks and data-state are chargebee's; re-check the
          // tree when chargebee.js moves.
          '#payment-container': {
            padding: '0',
          },
          '.payment-method-wrapper': {
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
          },
          '.payment-method-wrapper > div': {
            background: GP_DEPTH_2,
            border: `2px solid ${GP_DEPTH_3}`,
            borderRadius: '8px',
            overflow: 'hidden',
            transition: 'all 200ms',
          },
          '.payment-method-wrapper > div:hover': {
            borderColor: palette.accent,
            background: palette.accentWash.hover,
          },
          '.payment-method-wrapper > div:has([data-state="open"])': {
            borderColor: palette.accent,
            background: palette.accentWash.open,
          },
          // The item's own frame and the divider between items; the row card
          // above replaces both.
          '.payment-method-wrapper > div [data-state]': {
            border: 'none',
            boxShadow: 'none',
            background: 'transparent',
          },
          '.payment-method-wrapper > div > .g-Separator': {
            display: 'none',
          },
          // Without this the Section's padding makes each row ~170px tall; the
          // trigger's own 56px min-height is the saved-method row height.
          '.payment-method-wrapper .g-Section': {
            paddingTop: '0',
            paddingBottom: '0',
          },
          // The header line padded like a saved-method row (px-4).
          '.payment-method-wrapper .payment-method-wrapper-icon': {
            padding: '0 16px',
          },
          '.payment-method-wrapper [role="region"]': {
            padding: '0 16px 16px',
          },
          // Inputs (card number, expiry, CVC, name, etc.).
          '.g-TextField, .g-Select, .g-NumberField': {
            background: palette.field.background,
            borderColor: palette.field.border,
            color: palette.text,
          },
          '.g-TextField:focus-within, .g-Select:focus-within, .g-NumberField:focus-within':
            {
              borderColor: palette.accent,
            },
          // Field labels + helper copy.
          '.g-Label': {
            color: palette.text,
          },
          '.g-HelperText': {
            color: palette.mutedText,
          },
          // Primary submit / Save / Pay button. Lettered like our own
          // common-button (uppercase, tracking-wider, bold), which sits in the
          // same place for the balance and saved methods.
          '.g-BaseButton:where(.g-variant-solid)': {
            background: palette.accent,
            color: palette.onAccent,
            fontWeight: '700',
            textTransform: palette.buttonLabel.textTransform,
            letterSpacing: palette.buttonLabel.letterSpacing,
          },
          '.g-BaseButton:where(.g-variant-solid):hover': {
            background: palette.accentHover,
          },
          // Chargebee's form-level error banner. It prints the message its
          // server sends verbatim — on gportal-test the raw, misspelled key
          // "AN_ERROR_OCCURED" — so it is hidden and the host shows our own
          // translated message (onPaymentError). Field errors are the
          // .g-ErrorMessage below and stay.
          '.g-CalloutRoot[data-test-id="error-message-root"]': {
            display: 'none',
          },
          // Error copy on invalid input.
          '.g-ErrorMessage': {
            color: palette.errorText,
          },
          // TV-1746 — the Card row header ([glyph][label][6 brand logos]) is a
          // non-wrapping flex row inside overflow:hidden, so the last logo is
          // clipped below ~430px. No Payment Components option controls it, but
          // chargebee injects style.rules verbatim into the component iframe, so
          // CSS reaches it.
          //
          // The rows below are written against the accordion header chargebee
          // renders in the component iframe:
          //
          //   .payment-method-wrapper                     accordion root
          //     div.card                                  one per method type
          //       …trigger…
          //         .payment-method-wrapper-icon           header flex row
          //           [radio]                              only if showRadioButtons
          //           span                                 Text ml=4 (the 16px gutter)
          //             div                                PaymentMethodIcon root
          //               div                              [glyph][label][logo strip]
          //
          // Only `.payment-method-wrapper*` is a class hook; below it the path
          // is positional because chargebee puts no class on those nodes. Both
          // kinds fail silently — the markup is in a cross-origin iframe, so
          // there is nothing to assert on and no error to capture; the icons
          // just clip again. Re-derive the tree from chargebee's own component
          // bundle (js.chargebee.com/components/v1/payment, payment-method-icon
          // .tsx) and re-check the header at ~390px when chargebee.js moves.
          //
          // Reclaim the 16px gutter, which is chargebee's own margin on the
          // label span and not the radio's — we don't render the radio.
          // :first-child so the rule stops matching (and the gutter comes back)
          // if showRadioButtons is ever turned on: the radio root is a div, so
          // the span is no longer the first child.
          '.payment-method-wrapper .payment-method-wrapper-icon > span:first-child':
            {
              marginLeft: '0',
            },
          // Let the header wrap instead of clipping. Only the Card row matches
          // (.card is the payment-method type), so the other methods keep their
          // single-line header — and only Card carries a logo strip anyway,
          // chargebee renders it for no other type.
          '.payment-method-wrapper .card .payment-method-wrapper-icon > span > * > *':
            {
              flexWrap: 'wrap',
            },
          // The label wrapper ships min-width:0, so it collapses instead of
          // pushing the (non-shrinking) logo strip down. Pinning it to its text
          // width makes the strip the item that wraps. The label itself is
          // white-space:nowrap from chargebee's own wrapTitle default, which no
          // option exposes — so if a localized label ever outgrows the row it
          // truncates exactly as it does today, with the strip still on its own
          // line rather than clipped.
          '.payment-method-wrapper .card .payment-method-wrapper-icon > span > * > * > *:nth-child(2)':
            {
              minWidth: 'max-content',
            },
          // Floor for when a whole line still isn't enough (narrowest viewports
          // / large font scaling). The strip otherwise keeps chargebee's own
          // 10px gap and natural width on both layouts.
          '.payment-method-wrapper .card .payment-method-wrapper-icon > span > * > * > *:last-child > *':
            {
              flexWrap: 'wrap',
              rowGap: '8px',
            },
          // Payment-method row icons. chargebee renders each method's icon as
          // an inline <svg id="<method>-icon"> that paints from a theme CSS
          // variable — the card's outline `stroke` from `--primary-11`, the
          // Klarna glyph's `fill` from `--icon-text`. We override the variable
          // scoped to the icon id (it inherits down to the child <path>, which
          // is what actually carries the paint) AND set the concrete stroke/
          // fill on that <path> as a fallback, since a rule on the <svg> itself
          // can't beat the child's own presentation attribute.
          '#card-icon': {
            '--primary-11': '#ffffff', // card outline → white
          },
          '#card-icon path': {
            stroke: '#ffffff',
          },
          '#klarna-icon': {
            '--icon-text': 'rgb(255, 168, 205)', // Klarna brand pink
          },
          '#klarna-icon path': {
            fill: 'rgb(255, 168, 205)',
          },
        },
      },
    };
    return {
      ...options,
      style: {
        ...options.style,
        rules: mergeCssRules(
          options.style?.rules ?? {},
          palette.chargebee.rules,
        ),
      },
    };
  }

  async function mountPaymentForm(
    args: MountPaymentFormArgs,
  ): Promise<MountPaymentFormHandle | null> {
    const isCancelled = args.isCancelled ?? (() => false);
    const paymentMethods = args.paymentMethods ?? PAYMENT_METHODS;
    // The Payment Components form renders its legal-entity text from the
    // business entity bound at Chargebee.init(), not from the intent object
    // passed to components.create(). Pull business_entity_id off the intent
    // (chargebeePaymentIntentFromProto puts it there, e.g. "GPORTAL_EU") and
    // init the instance with it so the shown entity matches the intent.
    // Treat "" (proto default for an unset entity — GetBusinessEntityID
    // returns "" for an unknown region) as undefined: both mean "site default
    // entity".
    const businessEntityId =
      typeof args.paymentIntent.business_entity_id === 'string' &&
      args.paymentIntent.business_entity_id
        ? args.paymentIntent.business_entity_id
        : undefined;

    let init: Awaited<ReturnType<typeof initChargebee>>;
    try {
      init = await initChargebee(businessEntityId, isCancelled);
    } catch (e) {
      reportChargebeeError(e, {
        extra: { context: 'useChargebeePayment.initChargebee' },
      });
      throw e;
    }
    if (!init) return null;
    const { instance, mountId } = init;
    const ownsChargebee = () => mountId === currentMount;

    // Pass the active i18n locale through so chargebee renders its method
    // labels and inline error copy in the user's language. `locale` was
    // captured synchronously at the top of useChargebeePayment(). The
    // surfaceColor is whatever the caller wants the chargebee canvas to
    // sit on; default is depth-1 (matches the checkout step).
    const themeOptions = gportalThemeOptions(locale.value, args.surfaceColor);
    let components: ChargebeeComponentsBuilder;
    try {
      components = instance.components(themeOptions);
    } catch (e) {
      reportChargebeeError(e, {
        extra: { context: 'useChargebeePayment.components()' },
      });
      throw e;
    }

    // close() on a chargebee component fires its onClose callback. We
    // call close() during a) post-success cleanup of the button, and
    // b) unmount of the whole form — neither of which is a user-driven
    // cancel, so these flags suppress the onClose passthrough that
    // would otherwise reach args.onClose (e.g. navigate-to-cancelled
    // in the checkout step).
    let succeeded = false;
    let tearingDown = false;
    function notifyClose() {
      if (succeeded || tearingDown) return;
      args.onClose?.();
    }

    // Forward-declared so the payment component's onSuccess closure can
    // .close() the button after submit succeeds — the button is created
    // further down once the payment component has mounted.
    let buttonComponent: ChargebeeComponentInstance | null = null;
    let paymentComponent: ChargebeeComponentInstance | null = null;

    // Closes both components and, while this mount still owns chargebee.js,
    // tears the instance down. Idempotent — Vue's onBeforeUnmount can fire
    // alongside a caller-initiated cleanup, and closing twice would re-trigger
    // onClose. `tearingDown` suppresses the onClose passthrough: this is a
    // teardown, not a user cancel.
    function teardown(context: string) {
      if (tearingDown) return;
      tearingDown = true;
      try {
        buttonComponent?.close();
      } catch (e) {
        reportChargebeeError(e, {
          extra: {
            context: `useChargebeePayment.${context}.paymentButton.close()`,
          },
        });
      }
      try {
        paymentComponent?.close();
      } catch (e) {
        reportChargebeeError(e, {
          extra: { context: `useChargebeePayment.${context}.payment.close()` },
        });
      }
      // A newer mount owns chargebee.js now: tearing it down would destroy
      // that form, not this one.
      if (!ownsChargebee()) return;
      try {
        window.Chargebee?.tearDown?.();
      } catch (e) {
        reportChargebeeError(e, {
          extra: {
            context: `useChargebeePayment.${context}.Chargebee.tearDown()`,
          },
        });
      }
    }

    // The host can go away, or another form take over, while a component
    // mounts. Either way this form is not the one to keep: it is closed —
    // tearing chargebee.js down only if it still owns it — and the caller
    // gets null instead of a handle.
    function abandoned(): boolean {
      if (!isCancelled() && ownsChargebee()) return false;
      teardown('abandoned');
      return true;
    }

    // Order context for the `payment` component: `additionalData` carries the
    // subscription for Klarna; `billingAddress` + `customer` PREFILL the billing form alternative methods (Klarna,
    // Alipay, ...) render inside Payment Components. These are prefill VALUES
    // (`context.*`), NOT a `form` field declaration — so methods that render
    // no billing form (card / PayPal) have nothing to render them into and
    // stay untouched. The `form.billingAddress` option would force the billing
    // form (and its phone field) onto every method, so it is deliberately not
    // set. Field visibility per method is governed by the Checkout settings in
    // the Chargebee site.
    const paymentContext: Record<string, unknown> = {};
    // Klarna via Stripe sends exactly two things when it confirms the intent:
    // its method type and a copy of `context.additionalData.subscription`
    // (chargebee.js passes `additionalData` through unvalidated, as a
    // free-form record). Chargebee turns that into the interval of the
    // recurring mandate it asks Stripe for, and Stripe refuses the attempt with
    // "Missing required param:
    // payment_method_options[klarna][subscriptions][0][interval]" without it.
    // Chargebee documents neither the field nor its shape; `frequencyUnit` /
    // `frequencyPeriod` is what Chargebee's own hosted checkout sends for
    // Klarna (the plan's period_unit / period, read from its hp_app bundle).
    // Re-check it when Chargebee's bundles move.
    if (args.subscription) {
      paymentContext.additionalData = {
        subscription: {
          frequencyUnit: args.subscription.periodUnit,
          frequencyPeriod: args.subscription.period,
        },
      };
    }
    if (args.billingAddress) {
      // Forward only the fields we actually hold. Keys already match
      // chargebee's billingAddress shape 1:1, so we copy the non-empty ones
      // straight through; empty values are dropped so we never overwrite a
      // chargebee-side default with a blank or trip a method's field
      // validation. countryCode is included (method eligibility reads it).
      const billingAddress: Record<string, string> = {};
      for (const [key, value] of Object.entries(args.billingAddress)) {
        if (value) billingAddress[key] = value;
      }
      if (Object.keys(billingAddress).length > 0) {
        paymentContext.billingAddress = billingAddress;
      }

      // Name and email are contact info, not part of the billing address, and
      // the method forms prefill them from `context.customer` — passing them
      // only on billingAddress leaves those fields blank.
      const customer: Record<string, string> = {};
      const { firstName, lastName, email } = args.billingAddress;
      if (firstName) customer.firstName = firstName;
      if (lastName) customer.lastName = lastName;
      if (email) customer.email = email;
      if (Object.keys(customer).length > 0) {
        paymentContext.customer = customer;
      }
    }

    try {
      paymentComponent = components.create<ChargebeeComponentInstance>(
        'payment',
        {
          paymentIntent: args.paymentIntent,
          // Stack the payment methods (Card, PayPal, Klarna, ...) as
          // collapsible rows instead of a horizontal tab strip. This is
          // what the chargebee docs call the "accordion" layout
          // (https://www.chargebee.com/checkout-portal-docs/payment-components.html#examples).
          // showRadioButtons is off so the row header is a glyph + label +
          // brand logos — the shape the TV-1746 style.rules in
          // gportalThemeOptions are written against.
          layout: { type: 'accordion', showRadioButtons: false },
          // Limited to and ordered by the caller's methods (PAYMENT_METHODS
          // by default). Smart Routing still drops whichever of them the site
          // has not enabled for this customer/currency/entity.
          paymentMethods: {
            allowed: paymentMethods,
            sortOrder: paymentMethods,
            ...(args.selectDefaultPaymentMethod === false
              ? { selectDefaultPaymentMethod: false }
              : {}),
          },
          // Only present when the caller supplied the subscription or a billing
          // address (checkout flow). Omitted entirely otherwise so we never send
          // an empty context object.
          ...(Object.keys(paymentContext).length > 0
            ? { context: paymentContext }
            : {}),
        },
        {
          onSuccess: async (intent) => {
            // Disarm the button immediately — chargebee has already
            // authorized the intent at this point, and the user must
            // not be able to click "Pay" again during the gRPC submit
            // window. We mark `succeeded` first so the buttonComponent
            // .close() below (and any teardown that follows) doesn't
            // re-emit onClose as if the user had cancelled.
            succeeded = true;
            args.onSubmitted?.();
            try {
              buttonComponent?.close();
            } catch (closeErr) {
              reportChargebeeError(closeErr, {
                extra: {
                  context:
                    'useChargebeePayment.onSuccess.closeButton (post-authorization)',
                },
              });
            }
            try {
              await args.onSuccess(intent.id);
            } catch (e) {
              reportChargebeeError(e, {
                extra: { context: 'useChargebeePayment.onSuccess handler' },
              });
              args.onError?.(e);
            }
          },
          // chargebee.js fires this for in-form payment failures —
          // declined cards, expired cards, invalid CVC, gateway
          // rejections, etc. These are recoverable: chargebee already
          // shows the error inline in its UI, and the user just needs
          // to fix the input + resubmit. We surface a toast for
          // visibility and deliberately do NOT call args.onError —
          // bubbling these up would switch the page to a fatal-error
          // state. args.onError stays reserved for true mount-time
          // failures (chargebee.js can't load, invalid intent shape,
          // ...) which throw out of mountPaymentForm and reach the
          // caller via the promise rejection.
          onError: (error) => {
            // A pre-submit gate (onButtonClick) blocked submission for a
            // validation reason, not a payment failure — no toast/Sentry; the
            // host renders its own inline message (e.g. the consent checkbox).
            if (isSubmitBlocked(error)) return;
            reportChargebeeError(error, {
              extra: { context: 'useChargebeePayment.payment.onError' },
            });
            if (args.onPaymentError) {
              args.onPaymentError();
              return;
            }
            pushErrorNotification({
              title: t('checkout/step/create/payment_error/title'),
              message: t('checkout/step/create/payment_error/fallback'),
            });
          },
          onClose: notifyClose,
          onPaymentMethodChange: (paymentMethod) => {
            const type = readPaymentMethodType(paymentMethod);
            if (type) args.onPaymentMethodChange?.(type);
          },
          // chargebee.js calls this when the user clicks the payment-button.
          // Returning a resolved Promise lets the submission proceed; reject to
          // block it (the reason flows into onError). The checkout step passes a
          // gate that rejects until the mandatory T&C/Privacy checkbox is ticked.
          onButtonClick: () =>
            (args.onButtonClick ?? (() => Promise.resolve()))(),
        },
      );
    } catch (e) {
      reportChargebeeError(e, {
        extra: { context: 'useChargebeePayment.components.create("payment")' },
      });
      teardown('teardownPartialMount');
      throw e;
    }

    // Once the payment component exists, any failure further down (mounting
    // it, or creating / mounting the payment-button) must close it and drop
    // the chargebee singleton before rethrowing — otherwise the caller never
    // gets an unmount handle and is left with a ghost iframe / a stale
    // chargebee instance that blocks the next mountPaymentForm().
    try {
      await paymentComponent.mount(args.paymentMount);
    } catch (e) {
      reportChargebeeError(e, {
        extra: { context: 'useChargebeePayment.payment.mount()' },
      });
      teardown('teardownPartialMount');
      throw e;
    }
    if (abandoned()) return null;

    // payment-button is the actual submit/pay button. It's a separate
    // chargebee component that talks to the `payment` component above —
    // without it there's no way for the user to finalize the intent.
    //
    // Per-method button-text override. chargebee.js reads the submit
    // label from `paymentMethods.options.{method}.text` on the
    // payment-button component (per the API ref at
    // chargebee.com/checkout-portal-docs/payment-components-api-ref.html)
    // — we replicate the same label across every allowed method, so the
    // user sees a consistent button regardless of which row they expand in
    // the accordion.
    const buttonOptions: Record<string, unknown> = args.submitButtonLabel
      ? {
          paymentMethods: {
            options: Object.fromEntries(
              paymentMethods.map((type) => [
                type,
                { text: args.submitButtonLabel },
              ]),
            ),
          },
        }
      : {};
    try {
      buttonComponent = components.create<ChargebeeComponentInstance>(
        'payment-button',
        buttonOptions,
        {
          // Same toast-instead-of-bubble policy as the payment
          // component's onError (see comment there) — payment-button
          // errors are recoverable user/gateway issues, not mount
          // failures.
          onError: (error) => {
            if (isSubmitBlocked(error)) return;
            reportChargebeeError(error, {
              extra: { context: 'useChargebeePayment.paymentButton.onError' },
            });
            if (args.onPaymentError) {
              args.onPaymentError();
              return;
            }
            pushErrorNotification({
              title: t('checkout/step/create/payment_error/title'),
              message: t('checkout/step/create/payment_error/fallback'),
            });
          },
          onClose: notifyClose,
        },
      );
    } catch (e) {
      reportChargebeeError(e, {
        extra: {
          context: 'useChargebeePayment.components.create("payment-button")',
        },
      });
      teardown('teardownPartialMount');
      throw e;
    }

    try {
      await buttonComponent.mount(args.buttonMount);
    } catch (e) {
      reportChargebeeError(e, {
        extra: { context: 'useChargebeePayment.paymentButton.mount()' },
      });
      teardown('teardownPartialMount');
      throw e;
    }
    if (abandoned()) return null;

    return {
      // close() removes each component's iframe from the DOM, and tearing
      // down the chargebee.init() singleton lets the next mountPaymentForm()
      // (e.g. reopening the support modal) start from a clean slate.
      unmount: () => teardown('unmount'),
    };
  }

  return { mountPaymentForm };
}
